import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  Divider,
  Link,
} from '@mui/material';
import { AppConfig } from 'src/config/AppConfig';
import { getInvitationById } from 'src/slices/filterSlice';
import { useAuth } from 'src/hooks/useAuth';
import { User } from 'src/types/user';
import { useTranslation } from 'react-i18next';
import AppIcons from 'src/utils/image';

const JoinApp: React.FC = () => {
  const { signUpWithGoogle, user, signUpWithEmail } = useAuth();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { activationCode } = useParams<{ activationCode: string }>();
  const [isLoading, setIsLoading] = useState(true);
  const [isUIDisabled, setIsUIDisabled] = useState(false);
  const [userInvitation, setUserInvitation] = useState({});

  useEffect(() => {
    console.log('====activationCode===', activationCode);
    const fetchData = async () => {
      try {
        const userInvitationData = await dispatch(
          getInvitationById({ activationCode }),
        ).unwrap();
        console.log('====userInvitationData===', userInvitationData);
        if (userInvitationData) {
          setUserInvitation(userInvitationData);
          setIsLoading(false);
        } else {
          throw new Error(
            'The activation link has expired, please contact the administrator.',
          );
        }
      } catch (error: any) {
        //dispatch(setToast({ message: error.message, type: 'error' }));
        setIsLoading(false);
      }
    };

    fetchData();
  }, [activationCode]);

  useEffect(() => {
    if (user) {
      //localStorage.getItem('lastVisitedPath') || '/home/transactions'
      navigate(localStorage.getItem('lastVisitedPath') || '/home/transactions');
    }
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);
    const email = userInvitation.email;
    const password = formData.get('password') as string;
    const firstName = formData.get('firstName') as string;
    const lastName = formData.get('lastName') as string;
    try {
      signUpWithEmail(
        {
          email,
          password,
          displayName: firstName + ' ' + lastName,
        },
        userInvitation,
        activationCode,
      );
      //navigate(localStorage.getItem('lastVisitedPath') || '/home/transactions');
    } catch (error) {
      console.error('Signup error:', error);
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 px-4 sm:px-6 lg:px-8">
      <div className="w-full max-w-md bg-white shadow-md rounded-lg p-8">
        <div className="flex justify-center items-center gap-4 mb-6">
          <img src={AppIcons.logoSymbol} alt="logoSymbol" className="w-10 h-auto" />
          <img src={AppIcons.logoText} alt="logoText" className="w-60 h-auto" />
        </div>

        <h2 className="text-center text-2xl font-extrabold text-gray-900 mb-6">
          {t('signup.signUp')}
        </h2>

        <button
          onClick={() => signUpWithGoogle(userInvitation, activationCode)}
          className="flex items-center justify-start w-full bg-blue-600 text-white font-semibold py-1 px-1 rounded-lg focus:outline-none focus:shadow-outline mb-6 hover:bg-blue-700"
        >
          <img
            src={AppIcons.googleLogo}
            alt="Google"
            className="h-14 w-14 mr-14 ml-0"
          />
          {t('signup.signUpWithGoogle')}
        </button>

        <div className="text-center text-gray-500 mb-4">
          {t('signup.or')}
          <span
            onClick={() => navigate('/login')}
            style={{ color: '#ff6d0f', cursor: 'pointer' }}
          >
            Login
          </span>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Password input */}
          <div>
            <label
              htmlFor="password"
              className="block text-gray-700 text-sm font-medium"
            >
              {t('signup.passwordLabel')}
            </label>
            <input
              id="password"
              name="password"
              type="password"
              required
              placeholder={t('signup.passwordPlaceholder')}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* First Name input */}
          <div>
            <label
              htmlFor="firstName"
              className="block text-gray-700 text-sm font-medium"
            >
              {t('signup.firstNameLabel')}
            </label>
            <input
              id="firstName"
              name="firstName"
              type="text"
              required
              placeholder={t('signup.firstNamePlaceholder')}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Last Name input */}
          <div>
            <label
              htmlFor="lastName"
              className="block text-gray-700 text-sm font-medium"
            >
              {t('signup.lastNameLabel')}
            </label>
            <input
              id="lastName"
              name="lastName"
              type="text"
              required
              placeholder={t('signup.lastNamePlaceholder')}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Submit button */}
          <div>
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-orange-500 text-white font-semibold py-3 px-4 rounded-lg hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-600"
            >
              {t('signup.signUpButton')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default JoinApp;
